import { useMemo } from 'react';

import { AiLoadingIndicator } from '@spotify/confidence-core-react';
import { ConversationsV2ChatState } from '@spotify/confidence-graphql';

import { useChatEventExtensions, useToolExtensions } from '../../extensions';
import {
  findLastThoughtProcessIdx,
  groupChatEvents,
  groupThoughtProcess,
  useCachedChatEvents,
  useChat,
  useStickToBottom,
} from '../../hooks';
import { buildInlineBlocksIndex } from '../AssistantMessage/inlineBlocks';
import { ThoughtProcess } from '../ThoughtProcess';
import { ToolCallGroup } from '../ToolCallGroup';
import { ChatContextProvider } from './ChatContext';
import {
  DEFAULT_EVENT_POLL_INTERVAL_MS,
  RUNNING_EVENT_POLL_INTERVAL_MS,
} from './constants';

export const ChildChatTranscriptView = ({
  childChatName,
  className,
  contentClassName,
  defaultExpanded,
  elevated,
}: {
  childChatName: string;
  className?: string;
  contentClassName?: string;
  defaultExpanded?: boolean;
  elevated?: boolean;
}) => {
  const { chat } = useChat({ name: childChatName });
  const isChildRunning = chat?.state === ConversationsV2ChatState.Running;
  const { chatEvents } = useCachedChatEvents(childChatName, {
    pollInterval: isChildRunning
      ? RUNNING_EVENT_POLL_INTERVAL_MS
      : DEFAULT_EVENT_POLL_INTERVAL_MS,
  });
  const toolExtensions = useToolExtensions();
  const chatEventExtensions = useChatEventExtensions();
  const { scrollRef, contentRef } = useStickToBottom();

  const displayItems = useMemo(
    () => groupChatEvents(chatEvents ?? [], toolExtensions),
    [chatEvents, toolExtensions],
  );

  const processedItems = useMemo(
    () => groupThoughtProcess(displayItems, isChildRunning, toolExtensions),
    [displayItems, isChildRunning, toolExtensions],
  );

  const lastThoughtProcessIdx = useMemo(
    () => findLastThoughtProcessIdx(processedItems),
    [processedItems],
  );

  const chatContextValue = useMemo(
    () => ({
      chatName: childChatName,
      chatEvents: chatEvents ?? [],
      inlineBlocks: buildInlineBlocksIndex(chatEvents ?? []),
    }),
    [childChatName, chatEvents],
  );

  return (
    <ChatContextProvider value={chatContextValue}>
      <div className={className} ref={scrollRef}>
        <div ref={contentRef} className={contentClassName}>
          {processedItems.map((item, idx) => {
            if (item.type === 'thought-process') {
              return (
                <ThoughtProcess
                  key={item.key}
                  items={item.items}
                  isActive={isChildRunning && idx === lastThoughtProcessIdx}
                  isInterrupted={false}
                  defaultExpanded={defaultExpanded}
                  elevated={elevated}
                />
              );
            }

            if (item.type === 'tool-group') {
              return <ToolCallGroup key={item.key} entries={item.entries} />;
            }

            const extension = chatEventExtensions.find(ext =>
              ext.predicate(item.event),
            );

            if (!extension) {
              return null;
            }

            return <div key={item.key}>{extension.render(item.event)}</div>;
          })}
          {isChildRunning &&
            processedItems[processedItems.length - 1]?.type !==
              'thought-process' && <AiLoadingIndicator />}
        </div>
      </div>
    </ChatContextProvider>
  );
};
